import { Quaternion, type Group } from 'three';
import { icoAnimation, faceMappings, physics } from './MagicBallConfig';

type AnimState = 'idle' | 'sinking' | 'rotating' | 'rising';

function easeInQuad(t: number): number {
  return t * t;
}

function easeOutBack(t: number): number {
  const c = 1.70158;
  const t1 = t - 1;
  return 1 + t1 * t1 * ((c + 1) * t1 + c);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export class IcosahedronController {
  private group: Group | null = null;
  private ballShellGroup: Group | null = null;
  private faceQuaternions: Quaternion[] = [];
  private _currentFace = 0;
  private targetFace = 0;
  private state: AnimState = 'idle';
  private stateTime = 0;
  private startQuat = new Quaternion();
  private targetQuat = new Quaternion();
  private springTarget = new Quaternion();

  private onFaceChanged: ((index: number) => void) | null = null;

  get isAnimating(): boolean {
    return this.state !== 'idle';
  }

  get currentFace(): number {
    return this._currentFace;
  }

  get faceCount(): number {
    return faceMappings.length;
  }

  set onFaceChange(cb: ((index: number) => void) | null) {
    this.onFaceChanged = cb;
  }

  init(group: Group, ballShellGroup: Group): void {
    this.group = group;
    this.ballShellGroup = ballShellGroup;

    this.faceQuaternions = faceMappings.map((f) => {
      const [x, y, z, w] = f.quaternion;
      return new Quaternion(x, y, z, w);
    });

    this._currentFace = 14;
    this.group.quaternion.copy(this.faceQuaternions[this._currentFace]);
  }

  showFace(index: number, skipSink: boolean = false): void {
    if (this.state !== 'idle' || !this.group) return;
    if (index < 0 || index >= this.faceQuaternions.length) return;
    if (index === this._currentFace) return;

    this.targetFace = index;
    this.startQuat.copy(this.group.quaternion);
    this.targetQuat.copy(this.faceQuaternions[index]);
    if (this.ballShellGroup) {
      this.targetQuat.premultiply(this.ballShellGroup.quaternion);
    }
    if (skipSink) {
      // Shake-triggered reveals: jump straight to the hidden position so
      // the ico vanishes the same frame the wobble starts. The rotating
      // phase then runs while occluded, and the rising phase pops the
      // new face back into the window after the wobble settles.
      this.group.position.y = icoAnimation.hiddenY;
      this.state = 'rotating';
    } else {
      this.state = 'sinking';
    }
    this.stateTime = 0;
  }

  showRandom(skipSink: boolean = false): void {
    if (this.faceQuaternions.length === 0) return;
    let next: number;
    do {
      next = Math.floor(Math.random() * this.faceQuaternions.length);
    } while (next === this._currentFace);
    this.showFace(next, skipSink);
  }

  update(delta: number): void {
    if (!this.group) return;

    if (this.state === 'idle') {
      this.applySpringFollow(delta);
      return;
    }

    this.stateTime += delta;

    switch (this.state) {
      case 'sinking': {
        const t = Math.min(this.stateTime / icoAnimation.sinkDuration, 1);
        this.group.position.y = lerp(icoAnimation.restY, icoAnimation.hiddenY, easeInQuad(t));
        this.applySpringFollow(delta);
        if (t >= 1) {
          this.state = 'rotating';
          this.stateTime = 0;
        }
        break;
      }

      case 'rotating': {
        const t = Math.min(this.stateTime / icoAnimation.rotateDuration, 1);
        this.computeSpringTarget();
        this.group.quaternion.slerpQuaternions(this.startQuat, this.springTarget, t);
        if (t >= 1) {
          this._currentFace = this.targetFace;
          this.state = 'rising';
          this.stateTime = 0;
        }
        break;
      }

      case 'rising': {
        const t = Math.min(this.stateTime / icoAnimation.riseDuration, 1);
        this.group.position.y = lerp(icoAnimation.hiddenY, icoAnimation.restY, easeOutBack(t));
        this.applySpringFollow(delta);
        if (t >= 1) {
          this.group.position.y = icoAnimation.restY;
          this.state = 'idle';
          this.onFaceChanged?.(this._currentFace);
        }
        break;
      }
    }
  }

  private computeSpringTarget(): void {
    const faceQ = this.faceQuaternions[this.state === 'idle' || this.state === 'rising' ? this._currentFace : this.targetFace];
    this.springTarget.copy(faceQ);
    if (this.ballShellGroup) {
      this.springTarget.premultiply(this.ballShellGroup.quaternion);
    }
  }

  private applySpringFollow(delta: number): void {
    if (!this.group) return;
    this.computeSpringTarget();
    const t = Math.min(1, physics.icoSpringSpeed * delta);
    this.group.quaternion.slerp(this.springTarget, t);
  }
}
